<template>
  <div class="login-page">
    <main class="login-shell">
      <section class="login-card">
        <div class="brand">
          <img class="brand-logo" src="@/static/logo.jpg" alt="OttDrama" />
          <h1>{{ $t('auth.loginTitle') }}</h1>
          <p>{{ $t('auth.loginSubtitle') }}</p>
        </div>

        <van-form class="login-form" @submit="handleLogin">
          <van-field
            v-model.trim="form.account"
            name="account"
            :label="$t('auth.account')"
            :placeholder="$t('auth.accountPlaceholder')"
            autocomplete="username"
            clearable
          />
          <van-field
            v-model="form.password"
            name="password"
            type="password"
            :label="$t('auth.password')"
            :placeholder="$t('auth.passwordPlaceholder')"
            autocomplete="current-password"
            clearable
          />

          <button
            class="login-btn"
            type="submit"
            :disabled="!agreed || loading"
            :class="{ disabled: !agreed || loading }"
          >
            {{ loading ? $t('auth.loginLoading') : $t('auth.loginAction') }}
          </button>
        </van-form>

        <div class="checkbox-wrapper" @click="toggleAgreement">
          <div class="checkbox-custom" :class="{ checked: agreed }"></div>
          <span class="agree-text">
            {{ $t('auth.agreementPrefix') }}
            <span class="policy-link" @click.stop.prevent="showPolicy">{{ $t("legal.policy") }}</span>
            {{ $t("legal.and") }}
            <span class="terms-link" @click.stop.prevent="showTerms">{{ $t("legal.terms") }}</span>
          </span>
        </div>

        <div class="register-entry">
          <span>{{ $t('auth.noAccount') }}</span>
          <button type="button" @click="goRegister">{{ $t('auth.goRegister') }}</button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { userLogin } from '@/apis/login.js'
import { Toast } from 'vant'

export default {
  name: 'OttDramaLogin',
  data() {
    return {
      agreed: true,
      loading: false,
      form: {
        account: '',
        password: ''
      }
    }
  },
  methods: {
    async handleLogin() {
      if (!this.agreed) {
        Toast(this.$t('auth.agreementRequired'))
        return
      }

      if (!this.form.account) {
        Toast(this.$t('auth.accountRequired'))
        return
      }

      if (!this.form.password) {
        Toast(this.$t('auth.passwordRequired'))
        return
      }

      try {
        this.loading = true
        const { data } = await userLogin({
          account: this.form.account,
          password: this.form.password
        })

        if (!data) {
          Toast(this.$t("login.errorAccount"))
          return
        }

        this.$setUserInfo(data.userinfo)
        localStorage.setItem('token', data.userinfo.token)
        this.$router.push('/')
      } catch (error) {
        Toast(this.$t("login.errorAccount"))
      } finally {
        this.loading = false
      }
    },
    toggleAgreement() {
      this.agreed = !this.agreed
    },
    showPolicy() {
      this.$router.push('/legal/privacy')
    },
    showTerms() {
      this.$router.push('/legal/terms')
    },
    goRegister() {
      this.$router.push('/base/register')
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.login-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 16% 10%, rgba(255, 255, 255, 0.95), transparent 28%),
    linear-gradient(145deg, #f7f8fb 0%, #eef1f5 48%, #ffffff 100%);
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
}

.login-shell {
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: 62px 20px 34px;
}

.login-card {
  width: 100%;
  max-width: 430px;
  align-self: flex-start;
  padding: 34px 22px 24px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 28px;
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
}

.brand {
  margin-bottom: 24px;
  text-align: center;
}

.brand-logo {
  width: 74px;
  height: 74px;
  margin-bottom: 16px;
  object-fit: cover;
}

.brand h1 {
  margin: 0;
  color: #111111;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.16;
  letter-spacing: 0;
}

.brand p {
  max-width: 280px;
  margin: 10px auto 0;
  color: #6e6e73;
  font-size: 14px;
  line-height: 1.55;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.login-form ::v-deep .van-cell {
  display: flex;
  align-items: center;
  min-height: 54px;
  padding: 9px 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.login-form ::v-deep .van-cell::after {
  display: none;
}

.login-form ::v-deep .van-field__label {
  display: flex;
  align-items: center;
  width: 72px;
  color: #3a3a3c;
  font-size: 14px;
  font-weight: 600;
}

.login-form ::v-deep .van-field__body {
  display: flex;
  align-items: center;
  min-height: 34px;
}

.login-form ::v-deep .van-field__control {
  color: #111111;
  font-size: 15px;
  line-height: 22px;
}

.login-form ::v-deep .van-field__control::placeholder {
  color: #b0b0b7;
}

.login-btn {
  width: 100%;
  height: 52px;
  margin-top: 8px;
  border: 0;
  border-radius: 999px;
  background: #111111;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 16px 30px rgba(17, 17, 17, 0.18);
}

.login-btn:active {
  transform: translateY(1px);
}

.login-btn.disabled {
  background: #c7c7cc;
  box-shadow: none;
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  min-height: 38px;
  margin-top: 18px;
  color: #6e6e73;
  cursor: pointer;
}

.checkbox-custom {
  position: relative;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 1px solid #c7c7cc;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.86);
  transition: all 0.18s ease;
}

.checkbox-custom.checked {
  border-color: #111111;
  background: #111111;
}

.checkbox-custom.checked::after {
  position: absolute;
  top: 1px;
  left: 5px;
  width: 5px;
  height: 10px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  content: "";
  transform: rotate(45deg);
}

.agree-text {
  flex: 1;
  color: #6e6e73;
  font-size: 13px;
  line-height: 1.45;
  user-select: none;
}

.policy-link,
.terms-link,
.register-entry button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #007aff;
  font-weight: 700;
}

.register-entry {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  color: #7a7a80;
  font-size: 14px;
}

.register-entry button {
  font-size: 14px;
}

@media (max-width: 380px) {
  .login-shell {
    padding-right: 14px;
    padding-left: 14px;
  }

  .login-card {
    padding: 30px 16px 22px;
    border-radius: 24px;
  }

  .brand h1 {
    font-size: 27px;
  }

  .login-form ::v-deep .van-field__label {
    width: 66px;
  }
}
</style>
